import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import * as sessionService from '../services/sessionService';
import * as workflowExecutionService from '../services/workflowExecutionService';
import { PhaseDependencyGraph } from '../components/sessions/PhaseDependencyGraph';
import { WorkflowComponent, WorkflowMode } from '../components/workflows/WorkflowComponent';
import RelatedExecutionsView from '../components/workflows/RelatedExecutionsView';

const WorkflowDetails = () => {
  const { workflowId } = useParams<{ workflowId: string }>();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  
  const [workflow, setWorkflow] = useState<any>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [sessionCreationError, setSessionCreationError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'workflow_replay'>('details');
  
  // Fetch workflow data
  useEffect(() => {
    const fetchWorkflow = async () => {
      if (!workflowId) return;
      
      try {
        setLoading(true);
        const data = await sessionService.getWorkflow(workflowId);
        setWorkflow(data);
      } catch (err) {
        setError("Failed to fetch workflow");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchWorkflow();
  }, [workflowId]);
  
  // Fetch available sessions (only runs once on mount)
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const data = await sessionService.listSessions();
        setSessions(data);
      } catch (err) {
        console.error("Failed to fetch sessions", err);
      }
    };
    
    fetchSessions();
  }, []); // No dependencies, only runs on mount

  // Set default session if needed (runs when sessions change)
  useEffect(() => {
    // Only set default if no session is selected and we have sessions available
    if (!selectedSessionId && sessions.length > 0) {
      setSelectedSessionId(sessions[0].id);
    }
  }, [sessions, selectedSessionId]);
  
  const handleStartExecution = async () => {
    if (!workflowId) {
      alert("Cannot execute workflow: Missing workflow ID");
      return;
    }
    
    try {
      let sessionIdToUse = selectedSessionId;
      
      // If auto-create is selected, try to create a new session
      if (selectedSessionId === 'auto-create') {
        setIsCreatingSession(true);
        setSessionCreationError(null);
        
        try {
          // Create a meaningful session name with workflow name, timestamp and random suffix
          const timestamp = new Date().toLocaleString().replace(/[/,:]/g, '-');
          const randomSuffix = Math.floor(Math.random() * 10000); // Add random number to reduce collision chance
          const sessionName = `Workflow: ${workflow.name} (${timestamp}-${randomSuffix})`;
        
          console.log(`Creating new session: ${sessionName}`);
        
          // Show a more detailed status message
          setSessionCreationError("Creating session (this may take a moment)...");
        
          const sessionResponse = await sessionService.createSession(sessionName);
        
          // Clear the status message
          setSessionCreationError(null);
          
          // Log the full response for debugging
          console.log("Session creation response:", sessionResponse);
          
          // Extract the session ID from the response
          let sessionId;
          if (typeof sessionResponse === 'object') {
            // Try different possible property names for the session ID
            sessionId = sessionResponse.id || sessionResponse.session_id || sessionResponse.sessionId;
            
            if (!sessionId && sessionResponse.response) {
              // Check if ID is in a nested response property
              sessionId = sessionResponse.response.id || sessionResponse.response.session_id;
            }
          }
          
          if (!sessionId) {
            console.error("Invalid session creation response:", sessionResponse);
            throw new Error("No session ID returned from server");
          }
          
          sessionIdToUse = sessionId;
          
          console.log(`New session created with ID: ${sessionIdToUse}`);
          
          // Add the new session to the list for future reference
          setSessions(prev => [
            { id: sessionIdToUse, prompt: sessionName },
            ...prev
          ]);
        } catch (err) {
          console.error("Failed to create session:", err);
        
          // Provide a more helpful error message based on the error type
          if (err.response && err.response.status === 429) {
            setSessionCreationError("Server is busy. Please try again in a moment or use an existing session.");
          } else {
            setSessionCreationError(`Failed to create session: ${err.message || 'Unknown error'}`);
          }
        
          // Check if there are existing sessions to use as fallback
          if (sessions.length > 0) {
            sessionIdToUse = sessions[0].id;
            console.log(`Using existing session as fallback: ${sessionIdToUse}`);
            alert(`Failed to create a new session. Using an existing session instead.`);
          } else {
            setIsCreatingSession(false);
            return;
          }
        }
      } else if (!sessionIdToUse) {
        // If no session is selected (not even auto-create), show error
        alert("Please select a session or choose auto-create");
        setIsCreatingSession(false);
        return;
      }
      
      // Double-check that we have a valid session ID at this point
      if (!sessionIdToUse) {
        alert("Please select a session or choose auto-create");
        setIsCreatingSession(false);
        return;
      }
      
      // Start the workflow execution
      setLoading(true);
      console.log(`Starting workflow execution for workflow ${workflowId} with session ${sessionIdToUse}`);
      const result = await workflowExecutionService.startWorkflowExecution(
        workflowId,
        sessionIdToUse
      );
      
      console.log('Execution started successfully:', result);
      
      // Navigate to the execution view
      navigate(`/workflows/executions/${result.id}`);
    } catch (err) {
      console.error("Failed to start workflow execution:", err);
      alert(`Failed to start workflow execution: ${err.message || 'Unknown error'}`);
      setLoading(false);
      setIsCreatingSession(false);
    }
  };
  
  if (loading) return <div className="p-4">Loading workflow details...</div>;
  if (error) return <div className="p-4 text-red-600">Error: {error}</div>;
  if (!workflow) return <div className="p-4">No workflow data found</div>;
  
  return (
    <div className="p-4">
      <div className="mb-4">
        <h2 className="text-2xl font-bold">{workflow.name}</h2>
        <p className="text-gray-500">{workflow.description}</p>
      </div>
      
      {/* Add tab navigation */}
      {workflowId && (
        <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
          <ul className="flex flex-wrap -mb-px text-sm font-medium text-center">
            <li className="mr-2">
              <button 
                className={`inline-block p-4 border-b-2 rounded-t-lg ${
                  activeTab === 'details' 
                    ? `${darkMode ? 'text-blue-500 border-blue-500' : 'text-blue-600 border-blue-600'}`
                    : `${darkMode ? 'border-transparent hover:text-gray-300 hover:border-gray-300' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`
                }`}
                onClick={() => setActiveTab('details')}
              >
                Details
              </button>
            </li>
            <li className="mr-2">
              <button 
                className={`inline-block p-4 border-b-2 rounded-t-lg ${
                  activeTab === 'workflow_replay' 
                    ? `${darkMode ? 'text-blue-500 border-blue-500' : 'text-blue-600 border-blue-600'}`
                    : `${darkMode ? 'border-transparent hover:text-gray-300 hover:border-gray-300' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`
                }`}
                onClick={() => setActiveTab('workflow_replay')}
              >
                Workflow Replay
              </button>
            </li>
          </ul>
        </div>
      )}
      
      {activeTab === 'details' ? (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3">
            <WorkflowComponent 
              mode={WorkflowMode.VIEW}
              workflowId={workflowId}
              initialData={workflow}
              height="600px"
            />
          </div>
        
          <div className="lg:col-span-1 space-y-6">
            <div className={`rounded-lg shadow-md p-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className="text-lg font-medium mb-4">Execute Workflow</h3>
              
              <div className="mb-4">
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Select Session
                </label>
                <select
                  className={`w-full px-3 py-2 border rounded-md ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  value={selectedSessionId}
                  onChange={(e) => {
                    setSelectedSessionId(e.target.value);
                    setSessionCreationError(null); // Clear any previous errors
                  }}
                  disabled={isCreatingSession || loading}
                >
                  <option value="">Select a session...</option>
                  <option value="auto-create">Auto-Create New Session</option>
                  {sessions.map((session) => (
                    <option key={session.id} value={session.id}>
                      {session.prompt ? `${session.prompt.substring(0, 30)}...` : session.id}
                    </option>
                  ))}
                </select>
                
                {sessionCreationError && (
                  <p className="mt-1 text-sm text-red-600">{sessionCreationError}</p>
                )}
              </div>
              
              <button
                className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                onClick={handleStartExecution}
                disabled={!selectedSessionId || isCreatingSession || loading}
              >
                {isCreatingSession ? 'Creating Session...' : 
                 loading ? 'Starting Execution...' : 'Start Execution'}
              </button>
            </div>
            
            <div className={`rounded-lg shadow-md p-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className="text-lg font-medium mb-2">Workflow Details</h3>
              <div className="space-y-2">
                <div>
                  <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Created: </span>
                  <span>{new Date(workflow.created_at).toLocaleString()}</span>
                </div>
                <div>
                  <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Updated: </span>
                  <span>{new Date(workflow.updated_at).toLocaleString()}</span>
                </div>
                <div>
                  <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Phases: </span>
                  <span>{workflow.phases.length}</span>
                </div>
                <div>
                  <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Connections: </span>
                  <span>{workflow.connections.length}</span>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-4">
              <button
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={() => navigate(`/workflows/${workflowId}/edit`)}
              >
                Edit Workflow
              </button>
              <button
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                onClick={() => navigate('/workflows')}
              >
                Back to List
              </button>
            </div>
          </div>
        </div>
      ) : (
        // Workflow Replay tab content
        <div className="w-full">
          <RelatedExecutionsView
            workflowId={workflowId}
            darkMode={darkMode}
          />
        </div>
      )}
    </div>
  );
};

export default WorkflowDetails;
